// Admin console tiers — the client mirror of backend/api/admin/permissions/tiers.js.
// Pure (no store or React imports) so _sessionIds, hooks and tests can share it.
//
// eleplan-admin is Full access, read from the current account as it always
// has been. The tiers live on the user's own account membership (account_id =
// cognito_id), where set-admin-tiers writes them, so a tier holder's console
// calls go out with that account in the path. The server decides every call;
// this only picks the account and hides tabs a viewer can't use.

export type AdminAccess = 'ops' | 'memberData' | 'settings' | 'full';

export const FULL_ACCESS = 'eleplan-admin';
export const ADMIN_TIERS = ['admin-ops', 'admin-phi', 'admin-settings'] as const;
export type AdminTier = (typeof ADMIN_TIERS)[number];

const ROLE_SETS: Record<AdminAccess, readonly string[]> = {
  full: [FULL_ACCESS],
  ops: [FULL_ACCESS, ...ADMIN_TIERS],
  memberData: [FULL_ACCESS, 'admin-phi'],
  settings: [FULL_ACCESS, 'admin-settings'],
};

export const ADMIN_TIER_LABELS: Record<AdminTier, { label: string; description: string }> = {
  'admin-ops': {
    label: 'Operations',
    description: 'Metrics, health, costs, catalogs. No names or member content.',
  },
  'admin-phi': {
    label: 'Member data',
    description: 'See users, documents, chats and replays. Read only.',
  },
  'admin-settings': {
    label: 'Settings and approvals',
    description: 'Change system settings and approve rule, intent and tool proposals.',
  },
};

const permissionsOf = (account: any): string[] =>
  (Array.isArray(account?.permissions) ? account.permissions : []);

/** The tiers on a membership's permission list, in catalog order. */
export const tiersIn = (permissions: unknown): AdminTier[] =>
  (Array.isArray(permissions) ? ADMIN_TIERS.filter((tier) => permissions.includes(tier)) : []);

export interface AdminViewer {
  /** The roles the console acts with: ['eleplan-admin'], the tiers, or []. */
  permissions: string[];
  /** The account_id admin routes are called with. */
  accountId: string | undefined;
  fullAccess: boolean;
  hasConsoleAccess: boolean;
}

export const resolveAdminViewer = (state: any): AdminViewer => {
  const current = state?.session?.account;
  const cognitoId: string | undefined = state?.authentication?.user?.cognito_id;
  if (permissionsOf(current).includes(FULL_ACCESS)) {
    return { permissions: [FULL_ACCESS], accountId: current?.account_id, fullAccess: true, hasConsoleAccess: true };
  }
  const list: any[] = Array.isArray(state?.accounts?.list) ? state.accounts.list : [];
  const own = cognitoId
    ? [current, ...list].find((account) => account?.account_id && account.account_id === cognitoId)
    : undefined;
  const tiers = tiersIn(permissionsOf(own));
  return {
    permissions: tiers,
    accountId: tiers.length ? cognitoId : current?.account_id,
    fullAccess: false,
    hasConsoleAccess: tiers.length > 0,
  };
};

/** Tabs without an `access` are Full access: a new tab stays hidden from tiers until someone classifies it. */
export const canUseAdminAccess = (permissions: readonly string[], access: AdminAccess = 'full'): boolean =>
  permissions.some((permission) => ROLE_SETS[access].includes(permission));
